import { kstDayOf, type TodayScan } from './arrivalSortRules';

/** Where this browser keeps today's scans (rule 14); one key, replaced whole on every write. */
export const ARRIVAL_SORT_LOG_KEY = 'fulfillment.arrivalSort.log.v1';

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

interface StoredLog {
  day: string;
  scans: readonly TodayScan[];
}

const isScan = (value: unknown): value is TodayScan => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row['code'] === 'string' && typeof row['cls'] === 'string' && Array.isArray(row['orderIds']);
};

/** Today's scans, or none when the stored day is not today (KST) or the storage cannot be read. */
export const readTodayLog = (nowIso: string, storage: Pick<Storage, 'getItem'> | null = safeStorage()): TodayScan[] => {
  const today = kstDayOf(nowIso);
  try {
    const raw = storage?.getItem(ARRIVAL_SORT_LOG_KEY);
    if (!raw || !today) {
      return [];
    }
    const stored = JSON.parse(raw) as Partial<StoredLog>;
    if (stored.day !== today || !Array.isArray(stored.scans)) {
      return [];
    }
    return (stored.scans as unknown[]).filter(isScan);
  } catch {
    return [];
  }
};

/** Writes today's scans; a full or blocked storage leaves the desk working from memory rather than failing. */
export const writeTodayLog = (
  nowIso: string,
  scans: readonly TodayScan[],
  storage: Pick<Storage, 'setItem'> | null = safeStorage(),
): boolean => {
  const day = kstDayOf(nowIso);
  if (!day || !storage) {
    return false;
  }
  try {
    storage.setItem(ARRIVAL_SORT_LOG_KEY, JSON.stringify({ day, scans } satisfies StoredLog));
    return true;
  } catch {
    return false;
  }
};
